// Student.js
import React, { useState, useMemo, useEffect, use } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Grid,
  Card,
  CardContent,
  IconButton,
  Snackbar,
  Alert,
  Fab,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  InputAdornment,
  TablePagination,
  Avatar,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";

import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Boy as BoyIcon,
  Girl as GirlIcon,
  People as PeopleIcon,
  Class as ClassIcon,
} from "@mui/icons-material";

import { useNavigate, useLocation } from "react-router-dom";
import AddStudentForm from "./AddStudent";
import { useSnackbar } from "../../Components/Snackbar/SnackbarContext";
import DeleteConfirmDialog from "../DeleteConfirmDialog";
import apiEndpoints from "../../apiconfig";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { de } from "date-fns/locale";

const StudentListing = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterClass, setFilterClass] = useState("All");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(9);
  const [openForm, setOpenForm] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const { showSnackbar } = useSnackbar();
  const token = localStorage.getItem("token");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);

  const location = useLocation();
  const editId = new URLSearchParams(location.search).get("edit");
  const availableClasses = useMemo(() => {
    const set = new Set();
    students.forEach((s) => {
      if (s.class) set.add(s.class);
    });
    return ["All", ...Array.from(set)];
  }, [students]);

  const fetchStudents = async () => {
    try {
      const response = await fetch(`${apiEndpoints.student}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      console.log("Student data:", data);
      if (data.status === "success") {
        setStudents(
          data.data.map((s) => {
            // --- inside fetchStudents, for each student 's' ---

            return {
              id: s.id,
              guid: s.guid,
              firstName: s.first_name,
              lastName: s.last_name,
              gender: s.gender,
              dob: s.date_of_birth,
              admissionNumber: s.admission_number,
              photo: s.profile_photo,

              // 🔥 Updated according to PHP
              classGuid: s.class_guid,
              className: s.class_name,
              class: s.class_name,
              // section: s.section,
              branch: s.student_branch || s.branch_guid,

              address: s.parent_address || "",
              parentName: `${s.parent_first} ${s.parent_last}`,
              parentPhone: s.parent_mobile,
              parentEmail: s.parent_email,
              parent_photo: s.parent_photo || "",
              parent_guid: s.parent_guid,
              parent_password: s.parent_password,
              occupation: s.occupation || "",
              joiningDate: s.joining_date,
              bloodGroup: s.blood_group,

              previousSchool: s.previous_school || "",
              medicalInfo: s.medical_info || "",
              emergencyContact: s.emergency_contact || "",

              status:
                s.isActive == 1 || s.isActive === "Active"
                  ? "Active"
                  : "Inactive",
            };
          }),
        );
      }
    } catch (error) {
      showSnackbar("Network error. Please try again.", "error");
    }
  };

  useEffect(() => {
    fetchStudents(); // Load students second
  }, []);

  // ---------- Stats ----------
  const stats = useMemo(() => {
    const total = students.length;
    const active = students.filter((s) => s.status === "Active").length;
    const boys = students.filter((s) => s.gender === "Male").length;
    const girls = students.filter((s) => s.gender === "Female").length;

    return { total, active, boys, girls };
  }, [students]);

  // ---------- CRUD Handlers ----------
  const handleAdd = async (payload) => {
    try {
      const res = await fetch(`${apiEndpoints.student}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json(); // ✅ parse FIRST

      // 🔴 backend-level error (like class capacity)
      if (data.status !== "success") {
        showSnackbar(data.message || "Failed to add student", "error");
        return; // ⛔ stop here
      }

      // ✅ success
      showSnackbar("Student added successfully!", "success");
      setOpenForm(false);
      fetchStudents();
    } catch (err) {
      showSnackbar("Network error. Please try again.", "error");
    }
  };

  const handleUpdate = async (payload, guid) => {
    if (!guid) return showSnackbar("Missing student GUID", "error");
    try {
      const res = await fetch(`${apiEndpoints.student}?guid=${guid}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      console.log("BACKEND DATA:", data);
      if (data.status === "success") {
        showSnackbar(
          data.message || "Student updated successfully!",
          "success",
        );
        setOpenForm(false);
        fetchStudents();
      } else {
        showSnackbar(data.message || "Update failed", "error");
      }
    } catch {
      showSnackbar("Server error", "error");
    }
  };

  const handleViewStudent = (guid) => {
    navigate(`/admin/students/${guid}`);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setEditingStudent(null);
  };

  // ---------- Filtering & Search ----------
  const filteredStudents = useMemo(() => {
    return students.filter((student) => {
      if (filterClass !== "All" && student.class !== filterClass) return false;

      if (!searchTerm) return true;

      const searchableFields = [
        student.firstName,
        student.lastName,
        student.admissionNumber,
        student.class,
        student.parentName,
        student.parentEmail,
        student.parentPhone,
      ];

      return searchableFields.some((field) =>
        String(field || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
      );
    });
  }, [students, filterClass, searchTerm]);

  // ---------- Pagination ----------
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ---------- Status Badge ----------
  const StatusBadge = ({ status }) => (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 8px",
        borderRadius: "12px",
        fontSize: "0.7rem",
        fontWeight: 600,
        background:
          status === "Active"
            ? alpha(theme.palette.success.main, 0.15)
            : alpha(theme.palette.error.main, 0.15),
        color:
          status === "Active"
            ? theme.palette.success.main
            : theme.palette.error.main,
      }}
    >
      {status === "Active" ? (
        <CheckCircleIcon sx={{ fontSize: 12, mr: 0.5 }} />
      ) : (
        <CancelIcon sx={{ fontSize: 12, mr: 0.5 }} />
      )}
      {status}
    </Box>
  );
  const confirmDelete = async () => {
    if (!studentToDelete) return;

    const res = await fetch(`${apiEndpoints.student}?guid=${studentToDelete}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await res.json();

    if (data.status === "success") {
      showSnackbar(data.message || "Deleted successfully", "success");
      fetchStudents();
    } else {
      showSnackbar(data.message || "Delete failed", "error");
    }

    setDeleteDialogOpen(false);
    setStudentToDelete(null);
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setStudentToDelete(null);
  };

  const getStudentAvatarSrc = (photo) => {
    if (!photo) return undefined;

    // already valid
    if (photo.startsWith("http") || photo.startsWith("data:image")) {
      return photo;
    }

    // raw base64 from DB
    return `data:image/jpeg;base64,${photo}`;
  };

  const mapStudentForForm = (s) => {
    console.log("s", s);

    const [parentFirst = "", parentLast = ""] = (s.parent_name || "")
      .trim()
      .split(" ");

    return {
      // ids
      guid: s.guid,
      id: s.id || "",

      // student basic
      admission_number: s.admission_number || "",
      firstName: s.first_name || "",
      lastName: s.last_name || "",
      gender: s.gender || "",
      dob: s.date_of_birth || "",
      age: s.age || "",
      blood_group: s.blood_group || "",
      nationality: s.nationality || "",
      religion: s.religion || "",
      place_of_birth: s.place_of_birth || "",
      place_of_origin: s.place_of_origin || "",

      // class / branch
      class: s.class_guid || "",
      class_name: s.class_name || "",
      branch: s.branch_guid || "",
      joiningDate: s.joining_date || "",
      status: s.isActive == 1 ? "Active" : "Inactive",

      // profile
      profile_photo: s.profile_photo || "",
      remarks: s.remarks || "",

      // address
      address: s.address || "",

      // parent
      parent_guid: s.parent_guid || "",
      parent_first_name: parentFirst,
      parent_last_name: parentLast,
      parent_photo: s.parent_photo || "",
      parentPhone: s.parent_phone || "",
      parentEmail: s.parent_email || "",
      parentPassword: s.parent_password || "",
      occupation: s.occupation || "",
      qualification: s.qualification || "",
      placeofwork: s.placeofwork || "",
      username: s.username || "",

      // mother
      mother_first_name: s.mother_first_name || "",
      mother_last_name: s.mother_last_name || "",
      mother_mobile: s.mother_mobile || "",
      mother_email: s.mother_email || "",
      mother_qualification: s.mother_qualification || "",
      mother_work_place: s.mother_placeofwork || "",
      mother_address: s.mother_address || "",
      mother_occupation: s.mother_occupation || "",
      mother_tongue: s.mother_tongue || "",

      // medical
      allergy_status: s.allergy_status || "No",
      allergies: s.allergies || "",
      surgery_status: s.surgery_status || "No",
      surgeries: s.surgeries || "",
      chronic_illness_status: s.chronic_illness_status || "No",
      chronicIllness: s.chronicIllness || "",
      immunization_status: s.immunization_status || "",

      // emergency
      emergency_contact_name: s.emergency_contact_name || "",
      emergency_contact_phone: s.emergency_contact || "",
      emergency_relationship: s.emergency_contact_relation || "",
      emergency_contact_address: s.emergency_contact_address || "",

      // guardian
      guardian_name: s.guardian_name || "",
      guardian_relationship: s.guardian_relation || "",
      guardian_phone: s.guardian_phone || "",
      guardian_mobile: s.guardian_mobile || "",

      // previous school
      previously_attended_school:
        s.attended_school_before === "Yes" ? "yes" : "no",
      previous_school_name: s.previous_school || "",
      previous_school_duration: s.previous_school_duration || "",
      previous_school_class: s.previous_class_attended || "",

      // declaration
      declaration_parent_name: s.declaration_signed_by || "",
      declaration_date: s.declaration_date || "",
      declaration_signed_by: s.declaration_signed_by || "",

      // extra
      documents: s.documents || [],
      siblings: s.siblings || [],
      tuition_fee: s.tution_fee || "",
    };
  };

  const fetchStudentByGuid = async (guid) => {
    try {
      const res = await fetch(`${apiEndpoints.student}?guid=${guid}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();
      console.log("Fetched student for edit:", data);
      if (data.status === "success") {
        const mapped = mapStudentForForm(data.data);
        setEditingStudent(mapped);
        setOpenForm(true);
      } else {
        showSnackbar("Failed to load student details", "error");
      }
    } catch (err) {
      showSnackbar("Network error", "error");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        background: "rgba(255, 255, 255, 0.25)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        border: "1px solid rgba(255, 255, 255, 0.4)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: `
                radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.1) 0%, transparent 50%)
              `,
          pointerEvents: "none",
        },
      }}
    >
      {/* Main Content */}
      <Container
        maxWidth="100%"
        sx={{ py: 4, fontFamily: "Montserrat, sans-serif", minHeight: "100vh" }}
      >
        {/* Header */}
        <Box
          sx={{
            mb: 4,
            position: "relative",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight="700" gutterBottom>
              Student Directory
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage all enrolled students
            </Typography>
          </Box>

          {/* TOP-RIGHT FAB */}
          <Fab
            color="primary"
            size="medium"
            sx={{
              boxShadow: "0 6px 18px rgba(0,0,0,0.2)",
              flexShrink: 0,
              width: { xs: 48, sm: 56, md: 56 },
              height: { xs: 48, sm: 56, md: 56 },
              "& .MuiSvgIcon-root": {
                fontSize: { xs: "1.2rem", sm: "1.4rem", md: "1.6rem" },
              },
              mr: { xs: 1, sm: 0 },
            }}
            onClick={() => {
              setEditingStudent(null);
              setOpenForm(true);
            }}
          >
            <AddIcon />
          </Fab>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <Paper
              sx={{
                p: 2,
                borderRadius: "12px",
                background: "rgba(255,255,255,0.5)",
                boxShadow: "0 10px 24px rgba(15, 23, 42, 0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight="700" color="primary">
                  {stats.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Students
                </Typography>
              </Box>
              <PeopleIcon color="primary" />
            </Paper>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Paper
              sx={{
                p: 2,
                borderRadius: "12px",
                background: "rgba(255,255,255,0.5)",
                boxShadow: "0 10px 24px rgba(15, 23, 42, 0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight="700" color="success.main">
                  {stats.active}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active
                </Typography>
              </Box>
              <CheckCircleIcon color="success" />
            </Paper>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Paper
              sx={{
                p: 2,
                borderRadius: "12px",
                background: "rgba(255,255,255,0.5)",
                boxShadow: "0 10px 24px rgba(15, 23, 42, 0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Boys / Girls
                </Typography>
                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <BoyIcon fontSize="small" />
                    <Typography
                      variant="subtitle1"
                      fontWeight="700"
                      color="info.main"
                    >
                      {stats.boys}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <GirlIcon fontSize="small" />
                    <Typography
                      variant="subtitle1"
                      fontWeight="700"
                      color="info.main"
                    >
                      {stats.girls}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <ClassIcon color="info" />
            </Paper>
          </Grid>
        </Grid>

        {/* Control Bar: Search + Filter by Class */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            gap: 2,
            mb: 3,
          }}
        >
          <Box sx={{ flex: 1, minWidth: { xs: "100%", md: "45%" }, mb: 1 }}>
            <TextField
              placeholder="Search students..."
              variant="outlined"
              size="small"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                sx: {
                  bgcolor: "rgba(255,255,255,0.5)",
                  borderRadius: "999px",
                },
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              justifyContent: { xs: "flex-start", md: "flex-end" },
            }}
          >
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Filter by Class</InputLabel>
              <Select
                label="Filter by Class"
                value={filterClass}
                onChange={(e) => setFilterClass(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FilterIcon />
                    </InputAdornment>
                  ),
                  sx: {
                    bgcolor: "rgba(255,255,255,0.5)",
                    borderRadius: "999px",
                  },
                }}
                sx={{ bgcolor: "rgba(255,255,255,0.5)", borderRadius: "999px" }}
              >
                {availableClasses.map((cls) => (
                  <MenuItem key={cls} value={cls}>
                    {cls}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Student Grid */}
        {/* Student Grid */}
        <Grid container spacing={3}>
          {filteredStudents
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((student) => (
              <Grid
                item
                xs={12} // 1 per row on mobile
                sm={6} // 2 per row ≥600px
                md={6} // 2 per row on tablets (fix 1024px issue)
                lg={4} // 3 per row on large screens
                xl={3} // 4 per row on very large screens
                key={student.guid}
                sx={{ display: "flex" }}
              >
                <Card
                  sx={{
                    borderRadius: "14px",
                    p: { xs: 2, sm: 2.5, md: 3 },
                    minHeight: {
                      xs: 130,
                      sm: 150,
                      md: 160,
                      lg: 170,
                    },
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: { xs: 1.2, sm: 1.5, md: 2 },
                    background: "rgba(255,255,255,0.5)",
                    backdropFilter: "blur(16px)",
                    border: "1px solid rgba(255,255,255,0.5)",
                    boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
                    flex: 1,
                  }}
                >
                  {/* LEFT SECTION */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: 1.2, sm: 1.8 },
                      minWidth: 0,
                      flexWrap: { xs: "wrap", sm: "nowrap" },
                    }}
                  >
                    {/* Avatar */}
                    <Avatar
                      src={getStudentAvatarSrc(student.photo)}
                      sx={{
                        width: { xs: 50, sm: 60, md: 70 },
                        height: { xs: 50, sm: 60, md: 70 },
                        flexShrink: 0,
                      }}
                    >
                      {student.firstName?.[0]}
                    </Avatar>

                    {/* Student Text */}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          textTransform: "uppercase",
                          fontWeight: 700,
                          fontSize: { xs: "0.95rem", sm: "1rem" },
                          mb: 0.4,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: { xs: 120, sm: 160, md: 220 },
                        }}
                      >
                        {student.firstName} {student.lastName}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: { xs: "0.75rem", sm: "0.85rem" },
                          color: "text.secondary",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: { xs: 120, sm: 160, md: 220 },
                        }}
                      >
                        {student.class}
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: { xs: "0.7rem", sm: "0.75rem" },
                          color: "text.secondary",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: { xs: 120, sm: 160, md: 220 },
                        }}
                      >
                        Adm. No: {student.admissionNumber}
                      </Typography>

                      <Box sx={{ mt: 1 }}>
                        <StatusBadge status={student.status} />
                      </Box>
                    </Box>
                  </Box>

                  {/* RIGHT SECTION – ACTION BUTTONS */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 1.2,
                      minWidth: "40px",
                      pr: 1,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleViewStudent(student.guid)}
                      sx={{
                        bgcolor: "rgba(0,0,0,0.05)",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.1)" },
                      }}
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() => fetchStudentByGuid(student.guid)}
                      sx={{
                        bgcolor: "rgba(0,0,0,0.05)",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.1)" },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>

                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        setStudentToDelete(student.guid);
                        setDeleteDialogOpen(true);
                      }}
                      sx={{
                        bgcolor: "rgba(255,0,0,0.08)",
                        "&:hover": { bgcolor: "rgba(255,0,0,0.15)" },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Card>
              </Grid>
            ))}
        </Grid>

        {/* Pagination */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
          <TablePagination
            component="div"
            count={filteredStudents.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[9, 18, 36]}
          />
        </Box>
      </Container>

      {/* Modal Add/Edit Student Form */}
      {openForm && (
        <AddStudentForm
          mode={editingStudent ? "edit" : "add"}
          initialData={editingStudent}
          onSubmit={async (data, setServerError, studentGuid = null) => {
            try {
              const url = studentGuid
                ? `${apiEndpoints.student}?guid=${studentGuid}`
                : `${apiEndpoints.student}`;

              const method = studentGuid ? "PUT" : "POST";

              const res = await fetch(url, {
                method: method,
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(data),
              });

              const result = await res.json();

              if (result.status !== "success") {
                setServerError(result.message);
                showSnackbar(result.message, "error");
                return;
              }

              showSnackbar(
                studentGuid
                  ? "Student updated successfully!"
                  : "Student added successfully!",
                "success",
              );
              setOpenForm(false);
              fetchStudents();
            } catch (err) {
              setServerError("Network error. Please try again.");
              showSnackbar("Network error. Please try again.", "error");
            }
          }}
          onClose={handleCloseForm}
        />
      )}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title="Delete Student"
        message="Are you sure you want to delete this student? This action cannot be undone."
        onClose={cancelDelete}
        onConfirm={confirmDelete}
      />
    </Box>
  );
};

export default StudentListing;
